import { Box, Container, Stack, Button, Group, useMantineColorScheme, Loader, Center } from "@mantine/core";
import { useMeQuery, useUserTeamsQuery } from "../generated/graphql";
import useAuthUser from 'react-auth-kit/hooks/useAuthUser'
import UserProfileCard from "../components/UserProfileCard";
import UserNewFeed from "components/profile/UserNewFeed";
import { ArrowLeft } from 'tabler-icons-react';
import { Link } from "react-router-dom";
import { UserAuthData } from "../Types/UserDataTypes";

/**
 * UserNews page
 *
 * Expanded view for a user's news feed. The profile card is rendered at the top
 * and the list occupies the space below with scrolling when overflowed.
 */
export default function UserNews() {
  const { data, loading } = useMeQuery();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const authUser = useAuthUser<UserAuthData>();

  // Get the user's ID from auth context for fetching their teams
  const userId = authUser?.id ?? data?.me?.userData?.id;

  // Fetch teams that this user belongs to
  // TODO: This query filters teams by player membership, which is inefficient.
  // Consider adding a User.teams resolver on the backend for better performance.
  const { data: teamsData, loading: teamsLoading } = useUserTeamsQuery({
    variables: { userId: userId! },
    skip: !userId,
  });

  if (loading) {
    return (
      <Center style={{ minHeight: '50vh' }}>
        <Loader size="lg" />
      </Center>
    );
  }

  const displayName = data?.me?.userData?.name ?? authUser?.name ?? 'Player';
  const avatarUrl = data?.me?.userData?.avatarUrl ?? undefined;
  const roles = data?.me?.userData?.roles ?? authUser?.roles ?? [];

  // Get team info from the user's teams
  const userTeams = teamsData?.teams?.items ?? [];
  const primaryTeam = userTeams[0];
  const teamName = primaryTeam?.name;
  const franchiseName = primaryTeam?.franchise?.name;

  return (
    <Container size="lg" p="md">
      <Stack gap="md">
        <UserProfileCard
          avatarUrl={avatarUrl}
          name={displayName}
          title="Player"
          team={teamName}
          franchise={franchiseName}
          roles={roles as string[]}
        />

        {/* Back to home button: Uses theme-aware variant for good contrast in both schemes. */}
        {/* To change where this navigates, edit the `to` prop below. */}
        <Group justify="flex-start">
          <Button
            component={Link}
            to="/"
            leftSection={<ArrowLeft size={16} />}
            variant={isDark ? 'filled' : 'light'}
            size="xs"
            aria-label="Back to Home"
          >
            Back to Home
          </Button>
        </Group>

        {/* Scrollable list area */}
        <Box style={{ maxHeight: "65vh", overflowY: "auto" }}>
          {/* TODO: News feed - no news data source exists yet.
              When a news/announcements feature is implemented, wire this up.
              For now, showing empty state. */}
          <UserNewFeed
            title="Your News"
            items={[]}
          />
        </Box>
      </Stack>
    </Container>
  );
}
